/**
 * The parts of the Season Automations pages: the vertical steps with their counts, and the summaries beside them
 * (how it is doing, who is in it, what needs attention, which rooms it runs on).
 */
import { Link } from "react-router";
import type { AutomationDetail, AutomationPeople } from "../../shared/types";
import { SeasonCard } from "../components/season/Card";
import { GroupHead } from "../components/season/PageHead";
import { GroupedList, ListRow } from "../components/season/GroupedList";
import { Funnel } from "../components/season/Numbers";
import { type Editor, AskWords, askPaths, kindIcon, kindLabel, kindTone, stepFact, TagChip, waitWords } from "../components/FlowEditor";
import { Button } from "../components/ui/Button";
import { Face, FaceStack } from "../components/ui/Face";
import { Icon } from "../components/ui/Icon";
import { Toggle } from "../components/ui/Toggle";
import { attentionWords } from "../../shared/automation-words";
import type { Step } from "./flow-model";
import { howRows, people, whereWords, fmt } from "./words";

// --- the steps ---

function StepButton({ on, icon, tone, kind, title, count, waiting, onOpen }: { on: boolean; icon: Parameters<typeof Icon>[0]["name"]; tone: string; kind: string; title: React.ReactNode; count: string; waiting: boolean; onOpen: () => void }) {
  return (
    <li>
      <button type="button" className={on ? "au-s on" : "au-s"} onClick={onOpen}>
        <span className={`k-ico ${tone}`}>
          <Icon name={icon} />
        </span>
        <span className="au-st">
          <em>{kind}</em>
          <b>{title}</b>
          {count ? <span className={waiting ? "au-w" : undefined}>{count}</span> : null}
        </span>
        <Icon name="chev" />
      </button>
    </li>
  );
}

/** A step's title as the editor writes it: merge fields stay as `{{first name}}`. */
function stepTitle(step: Step): React.ReactNode {
  switch (step.type) {
    case "email":
      return step.subject.trim() || "No subject yet";
    case "room":
      return step.title.trim() || "An update with no title yet";
    case "fact":
      return (
        <>
          {step.op === "remove" ? "Remove" : "Add"} <TagChip name={step.name} />
        </>
      );
    case "webhook":
      return step.url.trim() || "No address yet";
    case "notify":
      return step.subject.trim() || "Update on {{first name}}";
    case "wait":
      return waitWords(step, true);
    case "branch":
      return <AskWords ask={step.ask} />;
  }
}

/** The steps in order, a check's two paths under it; the open one is lit. */
export function StepsLine({ editor, who, whenSub, open, onOpen }: { editor: Editor; who: AutomationPeople | null; whenSub: string; open: string | null; onOpen: (target: string) => void }) {
  const line = (list: Step[], nested: boolean) => (
    <ol className={nested ? "au-line au-in" : "au-line"}>
      {list.flatMap((s) => {
        const here = who?.at[s.uid]?.length ?? 0;
        const waiting = s.type === "wait" && here > 0;
        const row = (
          <StepButton
            key={s.uid}
            on={open === s.uid}
            icon={kindIcon(s.type)}
            tone={kindTone(s.type)}
            kind={kindLabel(s.type)}
            title={stepTitle(s)}
            count={waiting ? `${fmt(here)} waiting` : s.type === "wait" ? "" : stepFact(s, who)}
            waiting={waiting}
            onOpen={() => onOpen(s.uid)}
          />
        );
        if (s.type !== "branch") return [row];
        const paths = askPaths(s.ask);
        return [
          row,
          <li key={`${s.uid}-no`} className="au-arm au-no">
            <b>No</b> · {paths.no}
          </li>,
          ...(s.no.length ? [<li key={`${s.uid}-nos`}>{line(s.no, true)}</li>] : []),
          <li key={`${s.uid}-yes`} className="au-arm au-yes">
            <b>Yes</b> · {paths.yes}
          </li>,
          ...(s.yes.length ? [<li key={`${s.uid}-yess`}>{line(s.yes, true)}</li>] : []),
        ];
      })}
    </ol>
  );
  const when = editor.trigger;
  return (
    <div className="au-steps">
      <ol className="au-line">
        <StepButton
          on={open === "trigger"}
          icon={kindIcon("trigger")}
          tone={kindTone("trigger")}
          kind="When"
          title={
            <>
              Someone {when.type === "tag_removed" ? "loses" : "gets"} the tag <TagChip name={when.value} />
            </>
          }
          count={whenSub}
          waiting={false}
          onOpen={() => onOpen("trigger")}
        />
      </ol>
      {editor.steps.length ? line(editor.steps, false) : null}
    </div>
  );
}

// --- the summaries ---

export function HowCard({ d }: { d: AutomationDetail }) {
  const rows = howRows(d);
  if (!rows) return null;
  return (
    <>
      <GroupHead title="How it’s doing" />
      <SeasonCard>
        <Funnel className="au-fun" of={100} steps={rows.map(([label, shown, value]) => ({ label, shown, value }))} />
      </SeasonCard>
    </>
  );
}

export function NowCard({ who, editor }: { who: AutomationPeople | null; editor: Editor }) {
  const everyone = who ? Object.entries(who.at).flatMap(([node, list]) => list.map((p) => ({ p, node }))) : [];
  const rest = everyone.slice(4);
  return (
    <>
      <GroupHead title="In it right now" {...(everyone.length ? { count: people(everyone.length) } : {})} />
      {everyone.length ? (
        <SeasonCard flush>
          <GroupedList className="au-flat">
            {everyone.slice(0, 4).map(({ p, node }) => (
              <ListRow
                key={p.run_id}
                lead={<Face name={p.name || p.email} photo={p.avatar_url} size={44} />}
                title={p.name || p.email}
                sub={whereWords(p, node, editor)}
                to={`/people/${p.registrant_id}`}
              />
            ))}
          </GroupedList>
          {rest.length ? (
            <div className="au-more">
              <FaceStack people={rest.slice(0, 4).map(({ p }) => ({ name: p.name || p.email, photo: p.avatar_url }))} size={26} />
              <span>and {fmt(rest.length)} more</span>
            </div>
          ) : null}
        </SeasonCard>
      ) : (
        <SeasonCard>
          <p className="au-none">No one at the moment</p>
        </SeasonCard>
      )}
    </>
  );
}

/** What stopped, in the shipped words with the one reason when they all share it. */
export function attentionText(d: AutomationDetail): { words: string; reason: string } | null {
  const words = attentionWords(d.failed_runs);
  if (!words) return null;
  const reasons = [...new Set(d.failed_runs.map((r) => r.error.trim()).filter(Boolean))];
  return { words, reason: reasons.length === 1 ? `${reasons[0].replace(/\.$/, "")}.` : "" };
}

export function AttentionCard({ d, busy, onSettle }: { d: AutomationDetail; busy: boolean; onSettle: (how: "retry" | "stop") => void }) {
  const said = attentionText(d);
  return (
    <>
      <GroupHead title="Needs attention" />
      <SeasonCard className={said ? "au-attn warn" : "au-attn"}>
        {said ? (
          <>
            <span className="k-ico brass">
              <Icon name="info" />
            </span>
            <div className="au-attn-b">
              <p>
                <b>{said.words}.</b> {said.reason}
              </p>
              <div className="au-btns">
                <Button variant="p" disabled={busy} onClick={() => onSettle("retry")}>
                  Try again
                </Button>
                <Button disabled={busy} onClick={() => onSettle("stop")}>
                  Stop them
                </Button>
              </div>
            </div>
          </>
        ) : (
          <>
            <span className="k-ico good">
              <Icon name="check" />
            </span>
            <p className="au-clear">All clear.</p>
          </>
        )}
      </SeasonCard>
    </>
  );
}

export function RunsCard({ d, onRoom, onAdd }: { d: AutomationDetail; onRoom: (webinarId: string, on: boolean) => void; onAdd: () => void }) {
  const attached = d.webinars.filter((w) => w.attached);
  const others = d.webinars.filter((w) => !w.attached);
  return (
    <>
      <GroupHead
        title="Runs on"
        action={
          others.length ? (
            <button type="button" className="k-link" onClick={onAdd}>
              <Icon name="plus" /> Add a room
            </button>
          ) : null
        }
      />
      {attached.length ? (
        <ul className="k-gl au-runs">
          {attached.map((w) => (
            <li key={w.webinar_id} className="k-lr">
              <Link className="k-lr-b" to={`/rooms/${w.webinar_id}`}>
                <span className="au-pic">{w.poster_url ? <img src={w.poster_url} alt="" /> : <Icon name="pencil" />}</span>
                <span className="k-lr-t">
                  <b>{w.title}</b>
                  {w.enabled === 1 ? null : <span className="au-offhere">Off here</span>}
                </span>
              </Link>
              <Toggle on={w.enabled === 1} onChange={(on) => onRoom(w.webinar_id, on)} label={`On for ${w.title}`} />
            </li>
          ))}
        </ul>
      ) : (
        <SeasonCard>
          <p className="au-none">Not running on a room yet</p>
        </SeasonCard>
      )}
    </>
  );
}
